import { useQuery } from "@tanstack/react-query";

import type { KbVisibleScope } from "@/entities/knowledge";
import { orgService } from "@/entities/org-department/api/orgService";
import { Label } from "@/shared/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/shared/ui/select";

import {
  deptScopeAvailable,
  describeAudience,
  SHARED_SCOPES,
  VISIBLE_SCOPE_HINT,
  VISIBLE_SCOPE_LABEL,
} from "../lib/kbVisibleScope";

interface Props {
  scope: KbVisibleScope;
  deptId: number | null;
  onChange: (scope: KbVisibleScope, deptId: number | null) => void;
  disabled?: boolean;
}

/**
 * 「公司里谁能看到这份文档」选择器(2026-09-16)。
 *
 * 两个刻意的产品决定:
 * - **「指定部门」只在租户真有部门时才出现**。实测 33 个租户里只有 2 个建过部门树,其余都是
 *   系统自动建的一个根 —— 给它们亮出「指定部门」,选完等于没选。
 * - **选完立刻显示具体的人**,而不是只显示档位名。档位名会骗人(见 kbVisibleScope 注释)。
 */
export function KbVisibleScopeField({ scope, deptId, onChange, disabled }: Props) {
  const { data: depts = [] } = useQuery({
    queryKey: ["org", "departments"],
    queryFn: orgService.listDepartments,
    staleTime: 5 * 60 * 1000,
  });
  const { data: members = [] } = useQuery({
    queryKey: ["org", "all-members"],
    queryFn: orgService.listAllMembers,
    staleTime: 5 * 60 * 1000,
  });

  // 只有一个根部门 = 组织还没建起来,这一档选了也是空的(判据钉在 kbVisibleScope.deptScopeAvailable)
  const selectableDepts = depts.filter((d) => d.enabled);
  const deptOptionsAvailable = deptScopeAvailable(depts);
  const scopes = SHARED_SCOPES.filter((s) => s !== "DEPT" || deptOptionsAvailable);
  const audience = describeAudience(scope, deptId, depts, members);

  return (
    <div className="space-y-2">
      <Label htmlFor="kb-visible-scope">公司内可见范围</Label>
      <div className="flex flex-wrap gap-2">
        <Select
          value={scope}
          onValueChange={(v) => onChange(v as KbVisibleScope, v === "DEPT" ? deptId : null)}
          disabled={disabled}
        >
          <SelectTrigger id="kb-visible-scope" className="w-[160px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {scopes.map((s) => (
              <SelectItem key={s} value={s}>
                {VISIBLE_SCOPE_LABEL[s]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {scope === "DEPT" && (
          <Select
            value={deptId ? String(deptId) : ""}
            onValueChange={(v) => onChange("DEPT", Number(v))}
            disabled={disabled}
          >
            <SelectTrigger className="w-[220px]" aria-label="选择部门">
              <SelectValue placeholder="选择部门" />
            </SelectTrigger>
            <SelectContent>
              {selectableDepts.map((d) => (
                <SelectItem key={d.id} value={String(d.id)}>
                  {"　".repeat(Math.max(0, d.depth - 1))}
                  {d.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      <p className="text-xs text-muted-foreground" data-kb-scope-hint>
        {VISIBLE_SCOPE_HINT[scope]}
      </p>
      <p className="text-xs font-medium" data-kb-scope-audience>
        {audience.summary}
      </p>
    </div>
  );
}
